import React, { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { GameInvitationDoc, subscribeToMyGameInvitations, respondToGameInvitation } from '../firebase';
import { GameInviteBanner } from '../arena/components/HUD/GameInviteBanner';
import toast from 'react-hot-toast';

interface GameInviteContextType {
  pendingInvites: GameInvitationDoc[];
  dismissInvite: (inviteId: string) => Promise<void>;
}

const GameInviteContext = createContext<GameInviteContextType>({
  pendingInvites: [],
  dismissInvite: async () => {},
});

export const useGameInvites = () => useContext(GameInviteContext);

export const GameInviteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [pendingInvites, setPendingInvites] = useState<GameInvitationDoc[]>([]);
  const seenInviteIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!user?.uid) {
      setPendingInvites([]);
      seenInviteIdsRef.current.forEach((id) => toast.dismiss(`game-invite-${id}`));
      seenInviteIdsRef.current.clear();
      return;
    }

    const unsub = subscribeToMyGameInvitations(user.uid, (invitations) => {
      setPendingInvites(invitations);

      invitations.forEach((inv) => {
        // If this is a new invite we haven't toasted yet, fire custom banner toast
        if (!seenInviteIdsRef.current.has(inv.id)) {
          seenInviteIdsRef.current.add(inv.id);

          // Play subtle sound if available
          try {
            const audio = new Audio('/Images/notify.mp3');
            audio.volume = 0.7;
            audio.play().catch(() => {});
          } catch (_) {}

          // Present sticky custom toast
          toast.custom(
            (t) => <GameInviteBanner invite={inv} toastId={t.id} />,
            {
              id: `game-invite-${inv.id}`,
              duration: Infinity,
              position: 'top-center',
            }
          );
        }
      });

      // Cleanup dismissed toasts for items no longer in pending
      const currentIds = new Set(invitations.map((i) => i.id));
      seenInviteIdsRef.current.forEach((id) => {
        if (!currentIds.has(id)) {
          toast.dismiss(`game-invite-${id}`);
          seenInviteIdsRef.current.delete(id);
        }
      });
    });

    return () => {
      unsub();
      seenInviteIdsRef.current.forEach((id) => toast.dismiss(`game-invite-${id}`));
      seenInviteIdsRef.current.clear();
    };
  }, [user?.uid]);

  const dismissInvite = useCallback(async (inviteId: string) => {
    try {
      await respondToGameInvitation(inviteId, 'declined');
      toast.dismiss(`game-invite-${inviteId}`);
    } catch (e) {
      console.error('[GameInviteContext] Error dismissing invite:', e);
    }
  }, []);

  const value = useMemo(() => ({ pendingInvites, dismissInvite }), [pendingInvites, dismissInvite]);

  return (
    <GameInviteContext.Provider value={value}>
      {children}
    </GameInviteContext.Provider>
  );
};
